"use client";

import React, { createContext, useContext, useMemo } from "react";

import { MfaMethod } from "@/types";

import useSessionExpirationCheck from "./useSessionExpirationCheck";
import { useLocalStorageWithListener } from "@/components/utilities/useLocalStorage";
import useCookie from "@/components/utilities/useCookie";
import { useEnvironmentVariables } from "../environmentVariables";
import { getLocalDevSeededSession } from "./localDevSession";
import { getMockSeededSession } from "./mockSession";

interface Session {
	token: string;
	expires: string;
	mfa: {
		required: boolean;
		challengeToken: string;
		method?: MfaMethod | null;
	};
}

interface SessionContextValue {
	sessionToken: string;
	hasAuthenticatedSession: boolean;
	hasSessionExpired: boolean;
	isMfaRequired: boolean;
	mfaChallengeToken: string;
	mfaMethod: MfaMethod;
	createSession: (params?: { token?: string; expires?: string }) => void;
	removeSession: () => void;
	expireSession: () => void;
	setMfaChallenge: (params: {
		challengeToken: string;
		method?: MfaMethod | null;
	}) => void;
	clearMfaChallenge: () => void;
}

const SESSION_STORAGE_KEY = "session";
const sessionContext = createContext<SessionContextValue | undefined>(
	undefined,
);

export function useSession(): SessionContextValue {
	const context = useContext(sessionContext);

	if (!context) {
		throw new Error(
			"useSession should always be used within a SessionProvider",
		);
	}

	return context;
}

interface SessionProviderProps {
	children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({
	children,
	...rest
}) => {
	// Two independent seeds, resolved in order rather than merged into one
	// condition: local-dev injects a real minted JWT through its own env vars,
	// mock mode synthesises one from the mode alone. Neither can be reached in
	// the other's configuration, and runtimeMode throws if both are requested.
	const seededSession =
		getLocalDevSeededSession({
			runtimeEnvironment: process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT,
			token: process.env.NEXT_PUBLIC_LOCAL_DEV_SESSION_TOKEN,
			expires: process.env.NEXT_PUBLIC_LOCAL_DEV_SESSION_EXPIRES,
		}) ?? getMockSeededSession();

	const [storedSession = {}, setSession] = useLocalStorageWithListener(
		SESSION_STORAGE_KEY,
		{
			token: "",
			expires: "",
			mfa: {
				required: false,
				challengeToken: "",
			},
		},
	);

	// In local-dev, the freshly minted session injected via env is authoritative:
	// prefer it over any (possibly stale) session left in local storage by a prior
	// `pnpm dev` run or an earlier LOCAL_DEV_ACCOUNT_ID, so re-running the script
	// always picks up the newly minted persona. The app starts authenticated and
	// the login UI is skipped. Mock mode's seed takes precedence for the same
	// reason. No effect in any deployed environment (both seeds are null there).
	const session = seededSession ?? storedSession;

	const { cookieDomain } = useEnvironmentVariables();
	const [, setCookie, removeCookie] = useCookie("jasper_is_authenticated");
	const hasSessionExpired = useSessionExpirationCheck(session.expires);

	const value = useMemo(() => {
		const sessionToken = session.token;
		const hasAuthenticatedSession = Boolean(sessionToken);
		const isMfaRequired = session?.mfa?.required ?? false;
		const mfaChallengeToken = session?.mfa?.challengeToken ?? "";
		// A challenge stating no method is an emailed code: that is what every
		// challenge was before this field existed, so a blob written by an older
		// build reads correctly.
		const mfaMethod = session?.mfa?.method ?? MfaMethod.Email;

		return {
			sessionToken,
			hasAuthenticatedSession,
			hasSessionExpired,
			isMfaRequired,
			mfaChallengeToken,
			mfaMethod,

			createSession({ token = "", expires = "" } = {}) {
				setSession({
					token,
					expires,
					mfa: {
						required: false,
						challengeToken: "",
					},
				});
				setCookie(1, { expires: new Date(expires), domain: cookieDomain });
			},

			removeSession() {
				if (hasAuthenticatedSession) {
					setSession({
						token: "",
						expires: "",
						mfa: {
							required: false,
							challengeToken: "",
						},
					});
					removeCookie({ domain: cookieDomain });
				}
			},

			expireSession() {
				if (session.expires) {
					setSession((s: Session) => ({ ...s, expires: "" }));
				}
			},

			setMfaChallenge({
				challengeToken,
				method,
			}: {
				challengeToken: string;
				method?: MfaMethod | null;
			}) {
				setSession((s: Session) => ({
					...s,
					mfa: {
						...s.mfa,
						required: true,
						challengeToken,
						// Resending omits the argument and keeps the factor the challenge
						// was issued for. An explicit null is a new challenge that names
						// no method, which is the email flow, so it must clear a stored
						// AUTHENTICATOR rather than inherit it.
						method: method === undefined ? (s?.mfa?.method ?? null) : method,
					},
				}));
			},

			clearMfaChallenge() {
				setSession((s: Session) => ({
					...s,
					mfa: {
						required: false,
						challengeToken: "",
						method: null,
					},
				}));
			},
		};
	}, [
		session,
		hasSessionExpired,
		setSession,
		setCookie,
		removeCookie,
		cookieDomain,
	]);

	return (
		<sessionContext.Provider value={value} {...rest}>
			{children}
		</sessionContext.Provider>
	);
};
